import React from 'react';
import { useTranslation } from 'react-i18next';

import { useAppSelector } from '@/store';
import { selectDeployBlockedUnpaid } from '@/store/slices/session';
import { ArrowLeftIcon, GlobeIcon } from '../../components/icons';
import { useCustomizer } from '../../store/customizer';
import { MobileCta } from '../MobileCta';
import { MobileStepSlide } from '../MobileStepSlide';
import { Shell, TopBar, BackBtn, Progress, Bar, GlobeStep, Body } from './MobileChrome.styles';

/**
 * Mobile per-step chrome: a top bar (back arrow pinned left · centered progress
 * cluster of 3 bars + a globe for "go live"), the scrollable step body, and the
 * shared fixed CTA ("Continue to <next step>"). The body comes from the route.
 */
export const MobileChrome = ({
  children,
  entering = false,
}: {
  children: React.ReactNode;
  /** True only during the intro→rail morph: hides the real progress cluster so
   *  the flying ghost clones own the motion until the handoff. */
  entering?: boolean;
}): React.ReactElement => {
  const {
    step,
    totalSteps,
    step2Sub,
    connectStage,
    deployPhase,
    goNext,
    goBack,
    canAdvance,
    mainNextLabel,
    mainNextPlaceholders,
    connectLoading,
    startDeploy,
  } = useCustomizer();
  const { t } = useTranslation();
  // The deploy step body seeds + explains this; here we only need to know whether to refuse.
  const deployBlockedUnpaid = useAppSelector(selectDeployBlockedUnpaid);

  // Step 4 (deploy) owns its own footer (review CTA / done's two buttons), so the
  // shared CTA is suppressed. Back is hidden once deployment has started (one-way).
  const isDeploy = step >= totalSteps;
  const hideBack = isDeploy && deployPhase !== 'review';

  // Main-step CTA copy is shared with the desktop footer (`mainNextLabel`); only
  // the mobile-specific SUB-step CTAs (name→domain) and the connect sub-flow
  // override it here.
  let ctaLabel = mainNextLabel;
  let ctaPlaceholders: Record<string, string> | undefined = mainNextPlaceholders;
  if (connectStage === 'input') {
    ctaLabel = connectLoading ? t('Checking the available options to link your domain …') : t('Link your domain');
    ctaPlaceholders = undefined;
  } else if (connectStage === 'dns') {
    ctaLabel = t('Continue to Logo');
    ctaPlaceholders = undefined;
  } else if (step === 2 && (step2Sub === 'name' || step2Sub === 'own-name')) {
    ctaLabel = t('Continue to Domain Selection');
    ctaPlaceholders = undefined;
  }

  return (
    <Shell>
      <TopBar>
        {hideBack ? (
          <span aria-hidden />
        ) : (
          <BackBtn type="button" onClick={goBack} aria-label={t('Back')}>
            <ArrowLeftIcon size={24} />
          </BackBtn>
        )}
        <Progress $entering={entering}>
          {Array.from({ length: totalSteps - 1 }, (_, i) => (
            <Bar key={i} $on={i < step} data-chrome-stage={i} />
          ))}
          <GlobeStep $on={step >= totalSteps} data-chrome-stage={totalSteps - 1}>
            <GlobeIcon size={16} />
          </GlobeStep>
        </Progress>
      </TopBar>

      <Body $flush={isDeploy && deployPhase === 'done'}>
        {/* key={step} remounts the wrapper per step so each entrance replays; the
            chrome around it persists. The morph rail (in the fixed top bar above)
            is outside this slide, so it floats over the sliding body. */}
        <MobileStepSlide key={step} step={step}>
          {children}
        </MobileStepSlide>
      </Body>

      {isDeploy ? (
        // Step 4: the go-live review CTA sits at the SAME fixed-bottom position as
        // every other step's next button (green). Deploying/done footers are owned
        // by the deploy step body, so the shared CTA is suppressed there.
        deployPhase === 'review' ? (
          // Held back while the session's invoice is known to be unpaid — a bank-transfer
          // (SPEI) owner can reach onboarding before their money lands. The deploy step body
          // renders the notice explaining why; this just refuses to launch.
          <MobileCta
            label={mainNextLabel}
            green
            onClick={() => {
              if (deployBlockedUnpaid) return;
              startDeploy();
            }}
            disabled={deployBlockedUnpaid}
          />
        ) : null
      ) : (
        // `locked` while the morph plays: the intro plate above is non-interactive,
        // so without this a tap would fall through to here and advance the wizard
        // mid-flight. `locked` (not `disabled`) keeps the active orange fill so the
        // intro→step CTA cross-fade stays orange→orange (no grey flash).
        <MobileCta
          label={ctaLabel}
          placeholders={ctaPlaceholders}
          onClick={goNext}
          disabled={!canAdvance}
          locked={entering}
        />
      )}
    </Shell>
  );
};

export default MobileChrome;